import { $suggestions, Fragment } from '@bangle.io/prosemirror-plugins';
import { filterMentionCandidates } from '@bangle.io/supabase';
import { useAtomValue } from 'jotai';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { getMentionCandidates } from '../collab-state';
import { useEditorCoreServices } from '../use-editor-core-services';
import {
  FLOATING_INITIAL_STYLE,
  useFloatingPosition,
} from './use-floating-position';
import { useSuggestionUiHandler } from './use-suggestion-ui-handler';

const MAX_OPTIONS = 12;

export function UserMentionMenu({ editorName }: { editorName: string }) {
  const suggestions = useAtomValue($suggestions);
  const { editorEngine } = useEditorCoreServices();
  const listRef = useRef<HTMLDivElement>(null);
  const editorView = editorEngine.getEditor(editorName);
  const suggestion = editorView ? suggestions.get(editorView) : undefined;
  const active =
    suggestion?.markName === 'user_mention_suggestion' ? suggestion : undefined;
  const query = active?.text.slice(1) ?? '';

  const options = useMemo(
    () =>
      filterMentionCandidates(getMentionCandidates(), query).slice(
        0,
        MAX_OPTIONS,
      ),
    [query],
  );

  const selectedIndex = Math.max(
    0,
    Math.min(active?.selectedIndex ?? 0, Math.max(0, options.length - 1)),
  );

  const select = useCallback(
    (option: (typeof options)[number] | undefined) => {
      if (!editorView || !option) return;
      const node = editorView.state.schema.nodes.user_mention?.create({
        userId: option.id,
        label: option.label,
      });
      if (!node) return;
      editorEngine.extensions.mentionSuggestions.command.replaceSuggestMarkWith(
        {
          content: Fragment.from(node),
        },
      )(editorView.state, editorView.dispatch, editorView);
    },
    [editorView, editorEngine],
  );

  const selectedOption = options[selectedIndex];
  const mentionSuggestionUiHandler = useMemo(
    () => ({
      optionCount: options.length,
      onSelect: () => select(selectedOption),
    }),
    [options.length, select, selectedOption],
  );

  useSuggestionUiHandler({
    active: Boolean(active),
    editorView,
    handler: mentionSuggestionUiHandler,
    markName: 'user_mention_suggestion',
  });

  useEffect(() => {
    listRef.current
      ?.querySelector<HTMLElement>(`[data-option-index="${selectedIndex}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  const floatingRef = useFloatingPosition({
    show: Boolean(active?.show) && options.length > 0,
    anchorEl: () => active?.anchorEl() ?? null,
    boundaryElement: editorView?.dom ?? null,
  });

  if (!active?.show || !editorView || options.length === 0) return null;

  return (
    <div ref={floatingRef} style={FLOATING_INITIAL_STYLE}>
      <div
        ref={listRef}
        role="listbox"
        aria-label={t.app.editor.userMentionMenu.label}
        className="max-h-72 min-w-64 overflow-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
        data-testid="user-mention-menu"
      >
        {options.map((option, index) => (
          <button
            key={option.id}
            type="button"
            role="option"
            data-option-index={index}
            aria-selected={index === selectedIndex}
            className={`flex w-full flex-col items-start rounded-sm px-2 py-1.5 text-left text-sm ${
              index === selectedIndex ? 'bg-accent text-accent-foreground' : ''
            }`}
            onMouseDown={(event) => {
              event.preventDefault();
              select(option);
            }}
          >
            <span className="font-medium">{option.label}</span>
            {option.email && option.label !== option.email ? (
              <span className="text-muted-foreground text-xs">
                {option.email}
              </span>
            ) : null}
          </button>
        ))}
      </div>
    </div>
  );
}
